import { useState, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/use-auth';
import { ApiError, apiCall } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

function errorMessage(code: string): string {
  switch (code) {
    case 'INVALID_CURRENT_PASSWORD':
      return 'Current password is incorrect.';
    case 'USER_NOT_FOUND':
      return 'Your session is no longer valid. Please sign in again.';
    default:
      return 'Could not update profile. Please try again.';
  }
}

export function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Profile fields
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState('');
  const [whatsappConsent, setWhatsappConsent] = useState(false);

  // Track previous values to detect changes
  const phoneChanged = phone.trim() !== '';

  // UI state
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [showPhoneTooltip, setShowPhoneTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  async function handleProfileSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);

    setProfileBusy(true);
    try {
      await apiCall('/auth/profile', {
        method: 'PATCH',
        body: {
          firstName,
          lastName: lastName || undefined,
          phoneE164: phone || undefined,
          whatsappConsent: phoneChanged ? whatsappConsent : undefined,
        },
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setProfileError(errorMessage(err.errorCode));
        }
      } else {
        setProfileError('Network error. Try again.');
      }
    } finally {
      setProfileBusy(false);
    }
  }


  return (
    <div className="max-w-3xl py-2">
      <h1 className="text-2xl font-semibold mb-6">Profile & Settings</h1>
      <div className="space-y-6">
        {/* Profile Information Section */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {profileError && (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{profileError}</AlertDescription>
              </Alert>
            )}
            {profileSuccess && (
              <Alert variant="success" className="mb-4">
                <AlertTitle>Success</AlertTitle>
                <AlertDescription>Profile updated successfully</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleProfileSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">
                    First Name {profileError && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="e.g. John"
                    required
                    disabled={profileBusy}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lastName">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="e.g. Doe"
                    disabled={profileBusy}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email ?? ''}
                  readOnly
                  className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  To update your email address, please contact support
                </p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="phone">
                    Mobile/WhatsApp Number
                  </Label>
                  <div className="relative">
                    <button
                      type="button"
                      className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors cursor-pointer"
                      onMouseEnter={() => setShowPhoneTooltip(true)}
                      onMouseLeave={() => setShowPhoneTooltip(false)}
                      onFocus={() => setShowPhoneTooltip(true)}
                      onBlur={() => setShowPhoneTooltip(false)}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                      </svg>
                    </button>
                    {showPhoneTooltip && (
                      <div
                        ref={tooltipRef}
                        className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg z-10 pointer-events-none"
                      >
                        Add your number to receive class booking updates on WhatsApp
                        <div className="absolute top-full left-3 w-0 h-0 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                      </div>
                    )}
                  </div>
                </div>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 (555) 123-4567"
                  disabled={profileBusy}
                />
              </div>

              {/* Consents - Show based on field updates */}
              {phoneChanged && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                  {/* WhatsApp Consent - Only show if phone is not blank */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={whatsappConsent}
                      onChange={(e) => setWhatsappConsent(e.target.checked)}
                      disabled={profileBusy}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-700 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-300 transition-colors mt-px">
                      I agree to receive class booking updates and notifications via WhatsApp
                    </span>
                  </label>
                </div>
              )}

              <Button type="submit" disabled={profileBusy} className="w-full">
                {profileBusy ? 'Saving…' : 'Save Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
